import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grow,
  IconButton,
  makeStyles,
  Snackbar,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { getPaymentCompliance } from '../../services/payments';
import DeleteDialog from './DeleteDialog';
import LoadingComponentBlur from '../common/LoadingComponentBlur';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import StateNode from '../Dashboards/State/StateNode';
import DeleteIcon from '@material-ui/icons/Delete';
import ConfirmationDialog from '../AlertsAndReports/ConfirmationDialog';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { countNodes, deleteResource, getLists, getMaximumOfResource, validateCompliance } from './helpers';
import { Alert } from '@mui/material';
import UserContext from '../../context/UserContext';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 5,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  subtitle: {
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  resourceTitle: {
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  resourceList: {
    listStyle: 'none',
    padding: 5,
    width: '100%',
  },
  resourceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f5f5f5',
    padding: theme.spacing(1),
    borderRadius: '4px',
    marginBottom: theme.spacing(1),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  confirmButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));
const ComplianceModal = ({ handleClose, setOpenComplianceModal, handleLogout, lastPurchase, validateIfDowngrade, purchaseState }) => {
  const { plan } = useContext(UserContext);
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [status, setStatus] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [pendings, setPendings] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pendingKeyToDelete, setPendingKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [validatingCompliance, setValidatingCompliance] = useState(false);
  const LOCALIZACIONES = 'LOCALIZACIONES';
  const NODOS = 'NODOS';
  const SUBCUENTAS = 'SUBCUENTAS';
  const REPORTES = 'REPORTES';
  const EMAILS = 'EMAILS';
  const MEDIDORES = 'MEDIDORES';
  const PENDING_PAYMENT = 'Pendiente de Pago'
  const FREE = 'Gratuito'
  const [selectedItems, setSelectedItems] = useState({});
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [confirmationCallback, setConfirmationCallback] = useState(null);

  useEffect(() => {
    fetchCompliance();
    document.body.style.overflow = 'hidden'; // Evita scroll en el fondo
    return () => {
      document.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname === '/pagos') {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const fetchCompliance = async () => {
    setLoading(true);
    setCompliance(null);
    setSelectedItems({});
    setPendings([]);
    const res = await getPaymentCompliance();
    if (res?.data?.compliant && !res?.data?.downgrade_solicitado) {
      setOpenComplianceModal(false);
      validateIfDowngrade()
    } else {
      await getResourcesToDelete(res?.data?.pendientes);
      setCompliance(res?.data);
      setLoading(false);
    }
  };

  const getResourcesToDelete = async pendings => {
    if (!Array.isArray(pendings) || pendings.length === 0) {
      setLoading(false);
      return;
    }
    try {
      await Promise.all(pendings.map(item => getLists(item, setStatus, setPendings)));
    } catch (error) {
      console.error(error)
      setLoading(false);
    }
  };

  const handleDeleteMultipleSelected = async pendingKey => {
    if (!selectedItems[pendingKey] || selectedItems[pendingKey].length === 0) {
      return;
    }

    let actualItems = getResourceList(pendingKey)?.list?.length;
    let selectedItemsToDelete = selectedItems[pendingKey]?.length
    let remainingItems = actualItems - selectedItemsToDelete

    if (remainingItems < getMaximumOfResource(pendingKey, compliance)) {
      const confirmed = await confirmDialog();
      if (!confirmed) return;
    }

    setIsDeleting(true);
    try {
      const deletePromises = selectedItems[pendingKey].map(async resource => {
        await deleteResource(pendingKey, resource);
      });
      await Promise.all(deletePromises);
      setSnackbar({ open: true, message: 'Se eliminaron correctamente los recursos', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Ocurrió un error al eliminar el recurso', severity: 'error' });
      console.error("Error eliminando recursos:", error);
    }

    setIsDeleting(false);
    setSelectedItems(prev => ({ ...prev, [pendingKey]: [] }));
    await fetchCompliance();
  };

  const confirmDialog = () => {
    return new Promise(resolve => {
      setConfirmationCallback(() => result => resolve(result));
      setIsConfirmationDialogOpen(true);
    });
  };

  const handleIndividualDelete = async () => {
    if (!resourceToDelete || !pendingKeyToDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteResource(pendingKeyToDelete, resourceToDelete);
      setIsDeleting(false);
      setResourceToDelete(null);
      setPendingKeyToDelete(null);
      fetchCompliance();
      setSnackbar({ open: true, message: 'Se eliminó el recurso correctamente', severity: 'success' });
    } catch (error) {
      console.error(error)
      setSnackbar({ open: true, message: 'Ocurrió un error al eliminar el recurso', severity: 'error' });
      setIsDeleting(false);
    }
  }

  const handleOpenDeleteDialog = (pendingKey, resource) => {
    setPendingKeyToDelete(pendingKey);
    setResourceToDelete(resource);
    setIsDeleteDialogOpen(true);
  };

  const handleDialogConfirm = () => {
    setIsConfirmationDialogOpen(false);
    if (confirmationCallback) confirmationCallback(true);
  };

  const handleDialogCancel = () => {
    setIsConfirmationDialogOpen(false);
    if (confirmationCallback) confirmationCallback(false);
  };

  const getResourceList = pendingKey => {
    let pending = pendings?.find(pending => pending.name === pendingKey);
    return pending;
  };

  const handleCheckboxChange = (pendingKey, resource) => {
    setSelectedItems(prev => {
      const keyItems = prev[pendingKey] || [];
      const newItems = keyItems.includes(resource)
        ? keyItems.filter(item => item !== resource)
        : [...keyItems, resource];
      return { ...prev, [pendingKey]: newItems };
    });
  };

  const formatPlanName = planName => {
    let lastWord = planName.length
    planName = planName.toLowerCase()
    if (planName[lastWord - 1] === 'o') {
      return planName.slice(0, lastWord - 1) + 'a'
    }
    return planName
  }

  return ReactDOM.createPortal(
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        slots={{ transitions: Grow }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        handleConfirm={handleDialogConfirm}
        handleCancel={handleDialogCancel}
        title={`Estás eliminando más recursos de lo necesario. ¿Deseas continuar?`}
        content='En caso de confirmar se eliminarán todos los recursos seleccionados'
      />
      <DeleteDialog
        handleDeleteResource={handleIndividualDelete}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />
      <LoadingComponentBlur loadingState={isDeleting} />
      <Dialog open={true} fullWidth maxWidth="md">
        <Typography variant="h5" className={classes.dialogTitle}>
          {compliance && (compliance?.plan_actual === compliance?.proximo_plan ? `Tu cuenta excede los limites de tu suscripción ${formatPlanName(compliance?.plan_actual)}` : `Tu suscripción ${formatPlanName(compliance?.plan_actual)} venció!`)}
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography variant="body2" textAlign="center" style={{ fontStyle: 'italic' }}>
            {plan?.info?.email}
          </Typography>
        </Box>
        <DialogContent className={classes.dialogContent}>
          {(step === 1 && compliance?.proximo_plan === FREE) ? (
            <>
              <Box textAlign="center" width={'100%'}>
                <Typography textAlign="center" variant="body1">
                  {compliance &&
                    <>
                      {purchaseState === PENDING_PAYMENT ? (
                        'Tienes una compra pendiente de pago, finalizala haciendo click aquí'
                      ) : (
                        <>
                          {(!lastPurchase || compliance?.plan_actual === 'Gratuito') && (
                            'Podés contratar una nueva suscripción haciendo click aquí'
                          )}
                          {(compliance?.plan_actual !== 'Gratuito' && compliance?.plan_actual !== compliance?.proximo_plan) && (
                            'Podés renovar o contratar una nueva suscripción haciendo click aquí'
                          )}
                          {(compliance?.plan_actual !== 'Gratuito' && compliance?.plan_actual === compliance?.proximo_plan) && (
                            'Podés modificar tu suscripción haciendo click aquí'
                          )}
                        </>
                      )}
                    </>
                  }
                </Typography>
                <Box mt={2}>
                  {compliance && <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                  >
                    <Link to="/pagos" style={{ textDecoration: 'none', color: 'white' }}>
                      {(!lastPurchase || compliance?.plan_actual === 'Gratuito') && 'Contratar suscripción'}
                      {(compliance?.plan_actual !== 'Gratuito' && compliance?.plan_actual !== compliance?.proximo_plan) && 'Contratar/Renovar suscripción'}
                      {(compliance?.plan_actual !== 'Gratuito' && compliance?.plan_actual === compliance?.proximo_plan) && 'Modificar suscripción'}
                    </Link>
                  </Button>}
                </Box>
              </Box>
              <Box mt={4}>
                <Typography variant="body2">
                  Si lo deseas puedes continuar con la suscripción gratuita, para ello
                  debes eliminar recursos que exceden el límite actual.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {compliance?.pendientes?.length > 0 &&
                <>
                  <p className={classes.subtitle}>
                    Selecciona los recursos que deseas eliminar:
                  </p>
                  <Typography variant="body2" style={{ marginBottom: '20px' }}>
                    {compliance?.plan_actual === compliance?.proximo_plan ?
                      `Solo necesitas eliminar elementos si deseas permanecer en la suscripción ${formatPlanName(compliance?.proximo_plan)}.` :
                      `Solo necesitas eliminar elementos si deseas pasar a la suscripción ${formatPlanName(compliance?.proximo_plan)}.`}
                  </Typography>
                </>
              }
              {loading ? (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {compliance?.pendientes?.length === 0 &&
                    <>
                      <Typography align="center" variant="h6" style={{ marginTop: '20px' }}>
                        🎉 ¡Ya estás cumpliendo con los requisitos de la nueva suscripción!
                      </Typography>
                      <Typography align="center" variant="body1" style={{ marginTop: '20px' }}>
                        Vas a pasar a la suscripción <b>{formatPlanName(compliance?.proximo_plan)}</b>.
                        <p style={{ color: 'red' }}>Esto implica un cambio en las politicas de retención de datos de tus Powermeters que <b>no se puede revertir</b>.</p>
                      </Typography>
                    </>
                  }
                  {compliance?.pendientes?.map((pendingKey, index) => (
                    <Box key={index}>
                      <Box display={'flex'} justifyContent="end" marginY={2}>
                        {pendingKey !== NODOS && selectedItems[pendingKey]?.length > 0 && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleDeleteMultipleSelected(pendingKey)}
                          >
                            Eliminar seleccionados
                          </Button>
                        )}
                      </Box>
                      <Box my={2}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography className={classes.resourceTitle}>
                              {pendingKey.toLowerCase()[0].toUpperCase() +
                                pendingKey.toLowerCase().slice(1)}{' '}
                              -
                              <span
                                style={{ fontSize: '15px', marginLeft: '4px' }}
                              >
                                {pendingKey !== NODOS &&
                                  `(${getResourceList(pendingKey)?.list.length
                                  } de ${getMaximumOfResource(pendingKey, compliance)
                                  } permitidas)`}
                                {pendingKey === NODOS &&
                                  `(${countNodes(
                                    getResourceList(pendingKey)?.list
                                  )} de ${compliance.requisitos.max_nodes
                                  } permitidas)`}
                              </span>
                            </Typography>
                          </AccordionSummary>

                          <AccordionDetails>
                            <ul className={classes.resourceList}>
                              {getResourceList(pendingKey)?.list?.map(
                                (resource, index) =>
                                  pendingKey !== NODOS && (
                                    <li
                                      key={index}
                                      className={classes.resourceItem}
                                    >
                                      <Box>
                                        <Checkbox
                                          checked={selectedItems[pendingKey]?.includes(resource) || false}
                                          onChange={() => handleCheckboxChange(pendingKey, resource)}
                                        />
                                        {pendingKey === LOCALIZACIONES && (
                                          <span>{resource?.name}</span>
                                        )}
                                        {pendingKey === SUBCUENTAS && (
                                          <span>{resource?.subaccount}</span>
                                        )}
                                        {pendingKey === REPORTES && (
                                          <span>{resource?.report_name}</span>
                                        )}
                                        {pendingKey === EMAILS && (
                                          <span>{resource?.email}</span>
                                        )}
                                        {pendingKey === MEDIDORES && (
                                          <span>{resource?.nombre}</span>
                                        )}
                                      </Box>
                                      <IconButton
                                        onClick={() =>
                                          handleOpenDeleteDialog(
                                            pendingKey,
                                            resource
                                          )
                                        }
                                      >
                                        {pendingKey !== MEDIDORES ? <DeleteIcon color="secondary" /> : <LinkOffIcon color="inherit" />}
                                      </IconButton>
                                    </li>
                                  )
                              )}

                              {pendingKey === NODOS &&
                                getResourceList(pendingKey)?.list?.map(node => (
                                  <StateNode
                                    key={node.id}
                                    node={node}
                                    status={status}
                                    fromStateDashboard={false}
                                    handleSelectedNode={() => { }}
                                    handleDeleteNode={item =>
                                      handleOpenDeleteDialog(pendingKey, item)
                                    }
                                  />
                                ))}
                            </ul>
                          </AccordionDetails>
                        </Accordion>

                      </Box>
                    </Box>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Box display={'flex'} justifyContent="space-between" width="100%">
            <Button
              onClick={() => {
                handleLogout();
              }}
              size="small" variant="contained" color="secondary" >
              Cerrar Sesión
            </Button>

            <Box>
              {(step === 1 && compliance?.proximo_plan === FREE) ? (
                <Button
                  onClick={() => setStep(2)}
                >
                  Continuar
                </Button>
              ) : (
                <>
                  {compliance?.proximo_plan !== FREE ?

                    <Button
                    >
                      <Link to="/pagos" style={{ textDecoration: 'none', color: 'black',marginRight: '10px' }}>
                        Contratar nueva suscripción
                      </Link>
                    </Button>
                    :
                    <Button
                      style={{ marginRight: '10px' }}
                      onClick={() => setStep(1)}>
                      Volver
                    </Button>}
                  {compliance?.pendientes?.length === 0 && (
                    <>
                      {validatingCompliance ? (
                        <CircularProgress size={25} />
                      ) : (
                        <Button
                          className={classes.confirmButton}
                          onClick={() => validateCompliance(setValidatingCompliance, setOpenComplianceModal, handleClose, setSnackbar)}
                        >
                          Pasar a suscripción {formatPlanName(compliance?.proximo_plan)}
                        </Button>
                      )}
                    </>
                  )
                  }
                </>
              )}
            </Box>

          </Box>
        </DialogActions>
      </Dialog >
    </>,
    document.body
  );
};

export default ComplianceModal;
